import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";

type DotFrame = boolean[];

const IDLE: DotFrame = Array(16).fill(false);

const PATTERNS: { frames: DotFrame[]; intervalMs: number }[] = [
  // 0: 하이라이트 폭발
  {
    intervalMs: 180,
    frames: [
      [
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
      ],
      [
        false,
        true,
        true,
        false,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        false,
        true,
        true,
        false,
      ],
      [
        true,
        true,
        true,
        true,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        true,
        true,
        true,
        true,
      ],
      [
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
      ],
      [
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
      ],
      [
        false,
        true,
        true,
        false,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        false,
        true,
        true,
        false,
      ],
      [
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
      ],
    ],
  },
  // 1: 리듬 비트
  {
    intervalMs: 280,
    frames: [
      [
        true,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        true,
        true,
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
      [
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
      [
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        false,
        false,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
      [
        true,
        false,
        true,
        false,
        false,
        true,
        false,
        true,
        true,
        false,
        true,
        false,
        false,
        true,
        false,
        true,
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
    ],
  },
  // 2: 공포 떨림
  {
    intervalMs: 120,
    frames: [
      [
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
      ],
      [
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        false,
        true,
        false,
      ],
      [
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        true,
        false,
        false,
      ],
      [
        true,
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        true,
        false,
        true,
      ],
      [
        false,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        false,
        false,
      ],
      [
        true,
        false,
        false,
        true,
        false,
        true,
        false,
        false,
        false,
        false,
        true,
        false,
        true,
        false,
        false,
        false,
      ],
      [
        false,
        false,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        true,
        false,
        true,
        false,
        false,
        true,
        false,
      ],
      [
        true,
        true,
        false,
        false,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
        false,
        false,
        true,
      ],
      [
        false,
        false,
        false,
        true,
        true,
        false,
        true,
        false,
        false,
        false,
        true,
        true,
        false,
        true,
        false,
        false,
      ],
      [
        true,
        false,
        true,
        false,
        false,
        true,
        false,
        true,
        true,
        false,
        true,
        false,
        false,
        true,
        false,
        true,
      ],
    ],
  },
];

const DONATION_OPTIONS = [
  {
    titleKey: "live_sim_opt_1_title",
    descKey: "live_sim_opt_1_desc",
    amountKey: "live_sim_opt_1_amount",
    duration: 3200,
    strength: 180,
    video: "https://cdn.bhaptics.com/media/live/highlights.mp4",
    hapticEventKey: "live_sim_opt_1_haptic_event",
    donorNameKey: "live_sim_opt_1_donor",
    donationCoinsKey: "live_sim_opt_1_coins",
    donationMsgKey: "live_sim_opt_1_msg",
  },
  {
    titleKey: "live_sim_opt_2_title",
    descKey: "live_sim_opt_2_desc",
    amountKey: "live_sim_opt_2_amount",
    duration: 4000,
    strength: 130,
    video: "https://cdn.bhaptics.com/media/live/rhythm-video.mp4",
    hapticEventKey: "live_sim_opt_2_haptic_event",
    donorNameKey: "live_sim_opt_2_donor",
    donationCoinsKey: "live_sim_opt_2_coins",
    donationMsgKey: "live_sim_opt_2_msg",
  },
  {
    titleKey: "live_sim_opt_3_title",
    descKey: "live_sim_opt_3_desc",
    amountKey: "live_sim_opt_3_amount",
    duration: 5000,
    strength: 160,
    video: "https://cdn.bhaptics.com/media/live/horror.mp4",
    hapticEventKey: "live_sim_opt_3_haptic_event",
    donorNameKey: "live_sim_opt_3_donor",
    donationCoinsKey: "live_sim_opt_3_coins",
    donationMsgKey: "live_sim_opt_3_msg",
  },
  {
    titleKey: "live_sim_opt_4_title",
    descKey: "live_sim_opt_4_desc",
    amountKey: "live_sim_opt_4_amount",
    duration: null,
    strength: 200,
    video: "https://cdn.bhaptics.com/media/live/random.mp4",
    hapticEventKey: "live_sim_opt_4_haptic_event",
    donorNameKey: "live_sim_opt_4_donor",
    donationCoinsKey: "live_sim_opt_4_coins",
    donationMsgKey: "live_sim_opt_4_msg",
  },
] as const;

const CHARACTER_IMAGES = {
  idle: "/images/software/live/character-idle.png",
  bomb: "/images/software/live/character-bomb.png",
  rhythm: "/images/software/live/character-rhythm.png",
  horror: "/images/software/live/character-horror.png",
  random: "/images/software/live/character-random.png",
} as const;

type CharacterType = keyof typeof CHARACTER_IMAGES;
const OPTION_CHARACTER: CharacterType[] = [
  "bomb",
  "rhythm",
  "horror",
  "random",
];

const CHAT_USERS = [
  "지환",
  "정민",
  "민주",
  "CUTECAT12",
  "James",
  "さくら",
  "甘いパン",
];
const CHAT_COLORS = [
  "#d4bbff",
  "#ff7eb6",
  "#6fdc8c",
  "#fddc69",
  "#57e5e5",
  "#78a9ff",
  "#ff832b",
];
const CHAT_MSG_KEYS = [
  "live_sim_chat_1",
  "live_sim_chat_2",
  "live_sim_chat_3",
  "live_sim_chat_4",
  "live_sim_chat_5",
  "live_sim_chat_6",
  "live_sim_chat_7",
  "live_sim_chat_8",
  "live_sim_chat_9",
  "live_sim_chat_10",
  "live_sim_chat_11",
  "live_sim_chat_12",
  "live_sim_chat_13",
  "live_sim_chat_14",
  "live_sim_chat_15",
  "live_sim_chat_16",
] as const;

type Chat = { user: string; msg: string; color: string };
type AnimatedChat = Chat & { id: number };

const MAX_CHATS = 5;

/* ── HapticStrengthSlider ─────────────────────────────────────── */
const STRENGTH_GROUPS = [
  { dotClass: "bg-blue-40", dim: false },
  { dotClass: "bg-blue-40", dim: false },
  { dotClass: "bg-bhaptics-yellow", dim: false },
  { dotClass: "bg-bhaptics-yellow", dim: false },
  { dotClass: "bg-red-40", dim: false },
  { dotClass: "bg-red-40", dim: true },
] as const;

function HapticStrengthSlider({
  strength,
  label,
}: {
  strength: number;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-[4px]">
      <span className="whitespace-nowrap text-[14px] font-600 leading-[18px] tracking-[0.25px] text-white [text-shadow:0_0_4px_black,0_0_4px_black]">
        {label} {strength}%
      </span>
      <div className="flex items-center gap-[1.5px]">
        {STRENGTH_GROUPS.map((group, gi) => (
          <div
            key={gi}
            className={cn(
              "flex w-[16.5px] flex-wrap content-center items-center gap-[1.5px]",
              group.dim && "opacity-40",
            )}
          >
            {Array.from({ length: 8 }).map((_, di) => (
              <div
                key={di}
                className={cn(
                  "size-[3px] shrink-0 rounded-full",
                  group.dotClass,
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── VestGrid ─────────────────────────────────────────────────── */
function VestGrid({
  patternIdx,
  isVibrating,
}: {
  patternIdx: number;
  isVibrating: boolean;
}) {
  const [dots, setDots] = useState<DotFrame>(IDLE);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameRef = useRef(0);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!isVibrating) {
      setDots(IDLE);
      frameRef.current = 0;
      return;
    }
    const actualIdx =
      patternIdx === 3 ? Math.floor(Math.random() * 3) : patternIdx;
    const pattern = PATTERNS[actualIdx];
    frameRef.current = 0;
    intervalRef.current = setInterval(() => {
      setDots(pattern.frames[frameRef.current % pattern.frames.length]);
      frameRef.current += 1;
    }, pattern.intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isVibrating, patternIdx]);

  return (
    <div className="flex h-full w-full rounded-[16px] bg-coolgray-10 p-[10px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.15)]">
      <div className="grid w-full grid-cols-4 gap-[8px]">
        {dots.map((active, i) => (
          <div
            key={i}
            className={cn(
              "aspect-square rounded-full transition-all duration-200",
              active
                ? "scale-105 bg-blue-50 shadow-[0_0_0_2px_rgba(69,137,255,0.2)]"
                : "scale-100 bg-coolgray-30",
            )}
          />
        ))}
      </div>
    </div>
  );
}

/* ── CharacterImage ───────────────────────────────────────────── */
function CharacterImage({
  type,
  className,
}: {
  type: CharacterType;
  className?: string;
}) {
  return (
    <>
      {(Object.entries(CHARACTER_IMAGES) as [CharacterType, string][]).map(
        ([key, src]) => (
          <Image
            key={key}
            src={src}
            alt=""
            fill
            sizes="(max-width: 768px) 180px, 320px"
            className={cn(
              "transition-opacity duration-500",
              className ?? "object-contain",
              type === key ? "opacity-100" : "opacity-0",
            )}
          />
        ),
      )}
    </>
  );
}

/* ── DonationOverlay ──────────────────────────────────────────── */
function DonationOverlay({
  optIdx,
  vibrationKey,
  vibrationDuration,
}: {
  optIdx: number;
  vibrationKey: number;
  vibrationDuration: number;
}) {
  const { t } = useTrans(["software"]);
  const opt = DONATION_OPTIONS[optIdx];
  return (
    <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[12px] duration-300 animate-in fade-in">
      <HapticStrengthSlider
        strength={opt.strength}
        label={t("live_sim_haptic_strength")}
      />
      <div className="size-[120px] overflow-hidden rounded-[8px]">
        <video
          src={opt.video}
          autoPlay
          loop
          muted
          playsInline
          className="h-full w-full object-cover"
        />
      </div>
      <div className="relative flex items-center justify-center overflow-hidden rounded-[16px] border-4 border-bhaptics-yellow bg-coolgray-100 px-[40px] py-[12px]">
        <div
          key={vibrationKey}
          className="absolute left-0 top-0 h-full animate-progress-linear-forwards bg-bhaptics-yellow opacity-30"
          style={
            {
              ["--tw-anim-dur" as string]: `${vibrationDuration}ms`,
            } as React.CSSProperties
          }
        />
        <span className="relative text-[14px] font-600 leading-[18px] tracking-[0.25px] text-bhaptics-yellow">
          {t(opt.hapticEventKey)}
        </span>
      </div>
      <div className="flex flex-col items-center gap-[8px]">
        <div className="flex items-center gap-[8px] drop-shadow-[0_0_2px_black]">
          <span className="text-[14px] font-600 leading-[18px] tracking-[0.25px] text-green-40">
            {t(opt.donorNameKey)}
          </span>
          <span className="text-[14px] font-600 leading-[18px] tracking-[0.25px] text-bhaptics-yellow">
            {t(opt.donationCoinsKey)}
          </span>
        </div>
        <span className="text-[14px] font-600 leading-[18px] tracking-[0.25px] text-white [text-shadow:0_0_4px_black,0_0_4px_black]">
          {t(opt.donationMsgKey)}
        </span>
      </div>
    </div>
  );
}

/* ── DonateButton ─────────────────────────────────────────────── */
function DonateButton({
  onClick,
  label,
  className,
}: {
  onClick: () => void;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-[44px] bg-blue-60 font-600 text-white transition-colors",
        className,
      )}
    >
      {label}
    </button>
  );
}

/* ── Shared props ─────────────────────────────────────────────── */
interface SectionProps {
  selected: number;
  isVibrating: boolean;
  donatingIdx: number;
  vibrationKey: number;
  vibrationDuration: number;
  chats: AnimatedChat[];
  handleDonate: () => void;
  handleSelectOption: (i: number) => void;
}

/* ── Desktop ─────────────────────────────────────────────────── */
function Desktop({
  selected,
  isVibrating,
  donatingIdx,
  vibrationKey,
  vibrationDuration,
  chats,
  handleDonate,
  handleSelectOption,
}: SectionProps) {
  const { t } = useTrans(["software"]);
  return (
    <div className="hidden flex-row gap-[24px] md:flex">
      {/* 왼쪽: 시청자 화면 */}
      <div className="flex flex-1 flex-col gap-[20px]">
        <div className="flex h-[40px] w-fit items-center justify-center rounded-full bg-blue-10 px-[16px]">
          <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.1px] text-blue-60">
            {t("live_sim_viewer_screen")}
          </span>
        </div>

        <div className="flex flex-col gap-[8px]">
          {DONATION_OPTIONS.map((opt, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelectOption(i)}
              className={cn(
                "flex w-full items-center justify-between rounded-[16px] border p-[16px] text-left transition-colors",
                i === selected
                  ? "border-blue-60 bg-blue-10"
                  : "border-coolgray-20 bg-white hover:bg-coolgray-10",
              )}
            >
              <div className="flex items-center gap-[20px]">
                <div className="size-[77px] shrink-0 overflow-hidden rounded-[8px] bg-coolgray-10">
                  <video
                    src={opt.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex flex-col gap-[8px]">
                  <span className="whitespace-nowrap text-[14px] font-600 leading-[18px] tracking-[0.25px] text-coolgray-90">
                    {t(opt.titleKey)}
                  </span>
                  <span className="whitespace-nowrap text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
                    {t(opt.descKey)}
                  </span>
                </div>
              </div>
              <span className="whitespace-nowrap text-[14px] font-600 leading-[18px] tracking-[0.25px] text-blue-60">
                {t(opt.amountKey)}
              </span>
            </button>
          ))}
        </div>

        <DonateButton
          onClick={handleDonate}
          label={t("live_sim_donate_btn")}
          className="flex h-[54px] w-full items-center justify-center text-[16px] leading-[22px] tracking-[0.1px] hover:bg-blue-70 active:bg-blue-70"
        />
      </div>

      {/* 오른쪽: 스트리머 방송 화면 */}
      <div className="flex flex-col gap-[20px] md:w-[952px] md:shrink-0">
        <div className="flex h-[40px] w-fit items-center justify-center rounded-full bg-blue-10 px-[16px]">
          <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.1px] text-blue-60">
            {t("live_sim_streamer_screen")}
          </span>
        </div>

        <div className="relative aspect-video w-full overflow-hidden rounded-[16px]">
          <Image
            src="/images/software/live/simulation-game-bg.jpg"
            alt={t("live_sim_streamer_screen")}
            fill
            sizes="(max-width: 768px) 100vw, 952px"
            className="object-cover"
          />

          {/* LIVE 배지 */}
          <div className="absolute left-[24px] top-[24px] flex items-center justify-center rounded-[4px] bg-red-50 px-[8px] py-[4px]">
            <span className="text-[12px] font-600 leading-[16px] tracking-[0.1px] text-white">
              LIVE
            </span>
          </div>

          {/* 채팅 오버레이 */}
          <div className="absolute left-[20px] top-[200px] flex w-[280px] flex-col gap-[8px] overflow-hidden rounded-[16px] bg-gradient-to-r from-black to-transparent p-[16px]">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className="flex gap-[4px] duration-300 animate-in fade-in slide-in-from-bottom-2"
              >
                <span
                  className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.4px]"
                  style={{ color: chat.color }}
                >
                  {chat.user}
                </span>
                <span className="whitespace-nowrap text-[12px] font-400 leading-[16px] tracking-[0.4px] text-white">
                  {chat.msg}
                </span>
              </div>
            ))}
          </div>

          {/* 햅틱 vest 그리드 */}
          <div className="absolute right-[20px] top-[20px] size-[144px]">
            <VestGrid patternIdx={selected} isVibrating={isVibrating} />
          </div>

          {/* 캐릭터 — 항상 표시, 옵션별 이미지 crossfade */}
          <div className="absolute left-[632px] top-[215px] size-[320px]">
            <CharacterImage
              type={isVibrating ? OPTION_CHARACTER[donatingIdx] : "idle"}
            />
          </div>

          {/* 도네이션 알림 */}
          {isVibrating && (
            <DonationOverlay
              optIdx={donatingIdx}
              vibrationKey={vibrationKey}
              vibrationDuration={vibrationDuration}
            />
          )}
        </div>

        <p className="whitespace-pre-line text-center text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
          {t("live_sim_disclaimer")}
        </p>
      </div>
    </div>
  );
}

/* ── Mobile ──────────────────────────────────────────────────── */
function Mobile({
  selected,
  isVibrating,
  donatingIdx,
  vibrationKey,
  vibrationDuration,
  chats,
  handleDonate,
  handleSelectOption,
}: SectionProps) {
  const { t } = useTrans(["software"]);
  const imageRef = useRef<HTMLDivElement>(null);
  const streamerLabelRef = useRef<HTMLDivElement>(null);

  const handleDonateWithScroll = () => {
    streamerLabelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    handleDonate();
  };

  return (
    <div
      ref={imageRef}
      className="flex flex-col items-center gap-[24px] md:hidden"
    >
      <div
        ref={streamerLabelRef}
        className="flex h-[40px] w-fit scroll-mt-16 items-center justify-center rounded-full bg-blue-10 px-[16px]"
      >
        <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.1px] text-blue-60">
          {t("live_sim_streamer_screen")}
        </span>
      </div>

      {/* Full-width: game preview + chat (px-4 컨테이너 밖으로 돌출) */}
      <div className="-mx-4 w-[calc(100%+2rem)] overflow-hidden">
        <div className="relative">
          {/* 게임 화면 */}
          <div className="relative h-[200px] w-full overflow-hidden">
            <Image
              src="/images/software/live/simulation-game-bg.jpg"
              alt={t("live_sim_streamer_screen")}
              fill
              className="object-cover"
              sizes="100vw"
            />
            <div className="absolute left-[8px] top-[8px] flex items-center justify-center rounded-[4px] bg-red-50 px-[8px] py-[4px]">
              <span className="text-[12px] font-600 leading-[16px] tracking-[0.1px] text-white">
                LIVE
              </span>
            </div>

            {isVibrating && (
              <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[6px] duration-300 animate-in fade-in">
                <div className="size-[80px] overflow-hidden rounded-[8px]">
                  <video
                    src={DONATION_OPTIONS[donatingIdx].video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="relative flex items-center justify-center overflow-hidden rounded-[8px] border-2 border-bhaptics-yellow bg-coolgray-100 px-[20px] py-[4px]">
                  <div
                    key={vibrationKey}
                    className="absolute left-0 top-0 h-full animate-progress-linear-forwards bg-bhaptics-yellow opacity-30"
                    style={
                      {
                        ["--tw-anim-dur" as string]: `${vibrationDuration}ms`,
                      } as React.CSSProperties
                    }
                  />
                  <span className="relative whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.4px] text-bhaptics-yellow">
                    {t(DONATION_OPTIONS[donatingIdx].hapticEventKey)}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-[4px]">
                  <div className="flex items-center gap-[4px] drop-shadow-[0px_0px_1px_black]">
                    <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.4px] text-green-40">
                      {t(DONATION_OPTIONS[donatingIdx].donorNameKey)}
                    </span>
                    <span className="whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.4px] text-bhaptics-yellow">
                      {t(DONATION_OPTIONS[donatingIdx].donationCoinsKey)}
                    </span>
                  </div>
                  <span className="whitespace-nowrap text-center text-[12px] font-600 leading-[16px] tracking-[0.4px] text-white [text-shadow:0_0_2px_black,0_0_2px_black]">
                    {t(DONATION_OPTIONS[donatingIdx].donationMsgKey)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* 블러 하단 */}
          <div className="relative h-[160px] overflow-hidden">
            <Image
              src="/images/software/live/simulation-game-bg.jpg"
              alt=""
              fill
              className="object-cover"
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[20px]" />
            <div className="absolute -top-[20px] left-[30px] isolate z-[1] size-[180px] will-change-transform">
              <CharacterImage
                type={isVibrating ? OPTION_CHARACTER[donatingIdx] : "idle"}
                className="object-contain object-bottom"
              />
            </div>
            <div className="absolute right-[20px] top-[20px] isolate z-[1] size-[120px] will-change-transform">
              <VestGrid patternIdx={selected} isVibrating={isVibrating} />
            </div>
          </div>
        </div>

        {/* 채팅 — 고정 높이, 새 채팅이 아래서 위로 쌓임 */}
        <div className="flex h-[120px] w-full flex-col justify-end gap-[8px] overflow-hidden bg-coolgray-100 p-[12px]">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className="flex w-full shrink-0 items-start gap-[4px] duration-300 animate-in fade-in slide-in-from-bottom-2"
            >
              <span
                className="shrink-0 whitespace-nowrap text-[12px] font-600 leading-[16px] tracking-[0.4px]"
                style={{ color: chat.color }}
              >
                {chat.user}
              </span>
              <span className="min-w-0 truncate text-[12px] font-400 leading-[16px] tracking-[0.4px] text-white">
                {chat.msg}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 도네이션 옵션 */}
      <div className="flex w-full flex-col gap-[12px]">
        {DONATION_OPTIONS.map((opt, i) => (
          <button
            key={i}
            type="button"
            onClick={() => handleSelectOption(i)}
            className={cn(
              "flex w-full items-center justify-between rounded-[12px] border p-[12px] text-left transition-colors",
              i === selected
                ? "border-blue-60 bg-blue-10"
                : "border-coolgray-20 bg-white hover:bg-coolgray-10",
            )}
          >
            <div className="flex items-center gap-[12px]">
              <div className="size-[48px] shrink-0 overflow-hidden rounded-[8px] bg-coolgray-10">
                <video
                  src={opt.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col gap-[2px]">
                <span className="text-[14px] font-600 leading-[18px] tracking-[0.25px] text-coolgray-90">
                  {t(opt.titleKey)}
                </span>
                <span className="text-[12px] font-400 leading-[16px] tracking-[0.4px] text-coolgray-70">
                  {t(opt.descKey)}
                </span>
              </div>
            </div>
            <span className="shrink-0 whitespace-nowrap text-[14px] font-600 leading-[18px] tracking-[0.25px] text-blue-60">
              {t(opt.amountKey)}
            </span>
          </button>
        ))}

        <DonateButton
          onClick={handleDonateWithScroll}
          label={t("live_sim_donate_btn")}
          className="flex h-[40px] w-full items-center justify-center text-[12px] leading-[16px] tracking-[0.1px] hover:bg-blue-70 active:bg-blue-70"
        />
      </div>

      <p className="md:blick hidden whitespace-pre-line text-center text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
        {t("live_sim_disclaimer")}
      </p>
    </div>
  );
}

/* ── Export ──────────────────────────────────────────────────── */
import React from "react";

export default function LiveSimulationSection() {
  const { t } = useTrans(["software"]);
  const [selected, setSelected] = useState(0);
  const [isVibrating, setIsVibrating] = useState(false);
  const [donatingIdx, setDonatingIdx] = useState(0);
  const [vibrationKey, setVibrationKey] = useState(0);
  const [vibrationDuration, setVibrationDuration] = useState(3200);
  const [streamedChats, setStreamedChats] = useState<AnimatedChat[]>([]);
  const streamIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tRef = useRef(t);
  tRef.current = t;

  const randomChat = (): AnimatedChat => {
    const user = CHAT_USERS[Math.floor(Math.random() * CHAT_USERS.length)];
    const msgKey =
      CHAT_MSG_KEYS[Math.floor(Math.random() * CHAT_MSG_KEYS.length)];
    const color = CHAT_COLORS[Math.floor(Math.random() * CHAT_COLORS.length)];
    return {
      user,
      msg: tRef.current(msgKey),
      color,
      id: ++streamIdRef.current,
    };
  };

  useEffect(() => {
    setStreamedChats(Array.from({ length: 4 }, randomChat));
    return () => {
      if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
      if (chatStopTimerRef.current) clearTimeout(chatStopTimerRef.current);
    };
  }, []);

  const handleSelectOption = (i: number) => setSelected(i);

  const handleDonate = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
    if (chatStopTimerRef.current) clearTimeout(chatStopTimerRef.current);

    const opt = DONATION_OPTIONS[selected];
    const duration =
      opt.duration ??
      DONATION_OPTIONS[Math.floor(Math.random() * 3)].duration ??
      3200;
    setDonatingIdx(selected);
    setVibrationDuration(duration);
    setVibrationKey((k) => k + 1);
    setIsVibrating(true);
    timerRef.current = setTimeout(() => setIsVibrating(false), duration);

    // duration 동안만 채팅 추가
    chatIntervalRef.current = setInterval(() => {
      setStreamedChats((prev) => [
        ...prev.slice(-(MAX_CHATS - 1)),
        randomChat(),
      ]);
    }, 800);
    chatStopTimerRef.current = setTimeout(() => {
      if (chatIntervalRef.current) {
        clearInterval(chatIntervalRef.current);
        chatIntervalRef.current = null;
      }
    }, duration);
  };

  const sharedProps = {
    selected,
    isVibrating,
    donatingIdx,
    vibrationKey,
    vibrationDuration,
    chats: streamedChats,
    handleDonate,
    handleSelectOption,
  };

  return (
    <ComponentErrorBoundary>
      <section className="w-full bg-white">
        <div className="mx-auto w-full max-w-[1440px] px-4">
          <h2 className="mb-6 text-center text-[24px] font-400 leading-[32px] text-coolgray-90 md:mb-8 md:text-left md:text-[32px] md:leading-[40px]">
            {t("live_sim_heading")}
          </h2>
          <Desktop {...sharedProps} />
          <Mobile {...sharedProps} />
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
